import { createTheme, Theme, useMediaQuery } from '@mui/material'

/**
 * Centralized responsive breakpoint helpers built on MUI's theme breakpoints.
 *
 * These exist so responsive logic is consistent across the app rather than
 * scattered ad-hoc `useMediaQuery` calls. They are additive: desktop (md+)
 * behavior is unaffected — `useIsMobile()` and `useIsTablet()` only return
 * true below the md breakpoint.
 */

// Fallback used only when these hooks are rendered outside a ThemeProvider
// (e.g. heavily-mocked unit tests). In the running app the real theme is
// always supplied, so this never affects production breakpoints.
const fallbackTheme = createTheme()
const safeTheme = (theme: Theme | null | undefined): Theme =>
  theme?.breakpoints ? theme : fallbackTheme

/**
 * True on phone-sized viewports (< 600px, the sm breakpoint).
 * Use to switch to stacked / single-column / list layouts.
 */
export function useIsMobile(): boolean {
  return useMediaQuery<Theme>((theme) => safeTheme(theme).breakpoints.down('sm'))
}

/**
 * True on tablet-sized viewports (>= 600px and < 960px).
 * Phones return false here; use together with useIsMobile() when tablet needs
 * a distinct layout from both phone and desktop.
 */
export function useIsTablet(): boolean {
  return useMediaQuery<Theme>((theme) => safeTheme(theme).breakpoints.between('sm', 'md'))
}

/**
 * True on any viewport narrower than the md (desktop) breakpoint (< 960px).
 * This is the common "not desktop" check — phones and tablets both return true.
 * Desktop (md+) is unaffected.
 */
export function useIsCompact(): boolean {
  return useMediaQuery<Theme>((theme) => safeTheme(theme).breakpoints.down('md'))
}
